(() => {
  const MAX_LEN = 255;
  const entries = [];
  const debugParam =
    typeof window !== 'undefined' &&
    /(?:\?|&)fp_clarity_debug=1(?:&|$)/.test(window.location.search);

  function normalize(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX_LEN);
  }

  function callClarity(type, key, value) {
    if (typeof window.clarity !== 'function') return;
    if (value === undefined) {
      window.clarity(type, key);
      return;
    }
    window.clarity(type, key, value);
  }

  function record(entry) {
    entries.push(entry);
    if (debugParam || window.fpClarityDebug) {
      console.info('[fpClarity]', entry.type, entry.name || entry.key, entry.value || '');
    }
    return entry;
  }

  function event(name) {
    const eventName = normalize(name);
    if (!eventName) return null;
    const entry = record({ type: 'event', name: eventName, at: Date.now() });
    callClarity('event', eventName);
    return entry;
  }

  function set(key, value) {
    const tagKey = normalize(key);
    const tagValue = normalize(value);
    if (!tagKey) return null;
    const entry = record({ type: 'set', key: tagKey, value: tagValue, at: Date.now() });
    callClarity('set', tagKey, tagValue);
    return entry;
  }

  function publishShopifyEvent(name, tags) {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    const customData = {};
    if (tags && typeof tags === 'object') {
      Object.keys(tags).forEach((key) => {
        if (tags[key] == null || tags[key] === '') return;
        customData[key] = normalize(tags[key]);
      });
    }

    window.Shopify.analytics.publish(`fp:${normalize(name)}`, customData);
  }

  function track(name, tags) {
    const entry = event(name);
    if (tags && typeof tags === 'object') {
      Object.keys(tags).forEach((key) => {
        if (tags[key] == null || tags[key] === '') return;
        set(key, tags[key]);
      });
    }
    publishShopifyEvent(name, tags);
    return entry;
  }

  function bindDesignIdeaTracking() {
    document.addEventListener('click', (clickEvent) => {
      const link = clickEvent.target.closest('a[href*="tdaState"]');
      if (!link) return;
      if (!document.body.classList.contains('design-ideas')) return;

      const section = link.closest('.image-with-text, .design-image-with-text, .shopify-section');
      const heading = section?.querySelector('h2, h1, .image-with-text__heading');

      track('design_idea_opened', {
        design_idea: heading?.textContent || link.textContent,
        design_idea_cta: link.textContent,
      });
    });
  }

  function bindLoginTracking() {
    const loginRoot = document.querySelector('.customer.login');
    if (!loginRoot) return;

    const loginForm =
      loginRoot.querySelector('#customer_login') ||
      loginRoot.querySelector('form[action*="/account/login"]');
    loginForm?.addEventListener('submit', () => {
      track('login_submitted');
    });

    loginRoot.querySelector('a.password-recover')?.addEventListener('click', () => {
      track('login_forgot_password_clicked');
    });

    loginRoot.querySelector('.register-button a')?.addEventListener('click', () => {
      track('login_create_account_clicked');
    });

    const recoverForm =
      loginRoot.querySelector('#recover_customer_password') ||
      loginRoot.querySelector('form[action*="/account/recover"]');
    recoverForm?.addEventListener('submit', () => {
      track('login_password_recover_submitted');
    });
  }

  function bindRegisterTracking() {
    const registerRoot = document.querySelector('.customer.register');
    if (!registerRoot) return;

    const registerForm =
      registerRoot.querySelector('#create_customer') ||
      registerRoot.querySelector('form[action*="/account"]');
    registerForm?.addEventListener('submit', () => {
      track('register_submitted');
    });

    registerRoot.querySelector('.register-button a')?.addEventListener('click', () => {
      track('register_login_clicked');
    });

    if (registerRoot.querySelector('.form__message, ul.errors')) {
      track('register_failed');
    }
  }

  function bindConfiguratorLoad() {
    // Angle 3D meldet zwei Ereignisse: Option gewaehlt und in den
    // Warenkorb. Es meldet nicht, ob der Konfigurator ueberhaupt
    // nutzbar geworden ist.
    //
    // Damit sieht in der Auswertung jemand, dessen 3D-Ansicht haengen
    // bleibt, genauso aus wie jemand, den der Konfigurator nicht
    // interessiert hat: Seitenaufruf, danach nichts. Beides ist
    // Abbruch, aber nur eines davon ist ein Fehler, und die beiden
    // brauchen entgegengesetzte Massnahmen.
    //
    // Deshalb wird hier selbst gemessen: fertig ist der Konfigurator,
    // wenn die erste Optionsgruppe im Baum steht.
    if (!/\/products\//.test(window.location.pathname)) return;

    const OPTION = '.tda-customizer-customization-option';
    const FRIST = 20000;
    const start = performance.now();
    let gemeldet = false;
    let beobachter = null;
    let uhr = null;

    function melde(fertig) {
      if (gemeldet) return;
      gemeldet = true;
      if (beobachter) beobachter.disconnect();
      if (uhr) window.clearTimeout(uhr);
      const dauer = Math.round(performance.now() - start);

      // Der Wert geht als Zeichenkette durch die Pipeline, GA4 kann ihn
      // deshalb nicht mitteln. Die Spanne daneben ist als Dimension
      // sofort auswertbar und beantwortet die eigentliche Frage:
      // wie viele warten laenger, als jemand zu warten bereit ist.
      let spanne = '10s+';
      if (dauer < 2000) spanne = '0-2s';
      else if (dauer < 5000) spanne = '2-5s';
      else if (dauer < 10000) spanne = '5-10s';

      track(fertig ? 'configurator_ready' : 'configurator_load_failed', {
        configurator_load_ms: String(dauer),
        configurator_load_bucket: spanne,
      });
    }

    if (document.querySelector(OPTION)) {
      melde(true);
      return;
    }

    beobachter = new MutationObserver(() => {
      if (document.querySelector(OPTION)) melde(true);
    });
    beobachter.observe(document.body, { childList: true, subtree: true });

    uhr = window.setTimeout(() => melde(false), FRIST);

    // Wer vorher aufgibt und weggeht, zaehlt ebenfalls als Fehlschlag.
    // Ohne das fehlen genau die Faelle, auf die es ankommt.
    window.addEventListener(
      'pagehide',
      () => {
        if (!gemeldet) melde(false);
      },
      { once: true }
    );
  }

  function bindAngle3DTracking() {
    window.addEventListener('Angle3DOptionValueSelected', (event) => {
      const detail = event.detail || {};

      track('configurator_option_selected', {
        configurator_option_title: detail.customizationOptionTitle,
        configurator_option_id: detail.customizationOptionId,
        configurator_value_title: detail.optionValueTitle,
        configurator_value_id: detail.optionValueId,
      });
    });

    window.addEventListener('Angle3DAddToCart', (event) => {
      const detail = event.detail || {};

      track('configurator_add_to_cart_confirmed', {
        configurator_product_id: detail.productId,
        configurator_variant_id: detail.variantId,
      });
    });
  }

  window.fpClarity = {
    event,
    set,
    track,
    log() {
      return entries.slice();
    },
    reset() {
      entries.length = 0;
    },
    dump() {
      console.table(entries);
      return entries.slice();
    },
    simulateAll() {
      this.reset();
      track('questionnaire_started');
      track('questionnaire_step_reached', { questionnaire_step: '2' });
      track('questionnaire_answer_selected', {
        questionnaire_q1: 'Office and business meetings',
        questionnaire_q1_design: 'executive',
      });
      track('questionnaire_completed', { questionnaire_result: 'executive' });
      track('design_idea_opened', { design_idea: 'Executive Derby' });
      track('configurator_add_to_cart', { configurator_cart_replaced: 'false' });
      track('configurator_add_to_cart_cancelled');
      track('cart_size_tab_selected', { cart_size_tab: 'scan' });
      track('cart_scan_selected');
      track('cart_scan_qr_started');
      track('cart_scan_completed');
      track('cart_standard_size_selected', { cart_standard_size: '42' });
      track('cart_size_deferred', { cart_size_deferred: 'yes' });
      track('checkout_continued');
      track('login_submitted');
      track('login_forgot_password_clicked');
      track('login_create_account_clicked');
      track('login_password_recover_submitted');
      track('register_submitted');
      track('register_login_clicked');
      track('register_failed');
      track('configurator_option_selected', {
        configurator_option_title: 'Leather Color & Laces',
        configurator_option_id: 'leather-color-laces',
        configurator_value_title: 'Midnight black',
        configurator_value_id: 'leather-black',
      });
      track('configurator_add_to_cart_confirmed');
      return this.dump();
    },
  };

  function bindTracking() {
    bindDesignIdeaTracking();
    bindLoginTracking();
    bindRegisterTracking();
    bindConfiguratorLoad();
    bindAngle3DTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTracking);
  } else {
    bindTracking();
  }
})();
